import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppHeader } from "@/components/AppHeader";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SidebarProvider, useSidebar } from "@/context/SidebarContext";
import { Loader2 } from "lucide-react";
import AmelLoginPage from "@/pages/AmelLoginPage";
import ComenziPage from "@/pages/ComenziPage";
import ComandaDetaliuPage from "@/pages/ComandaDetaliuPage";
import DocumentePage from "@/pages/DocumentePage";
import ObloanePage from "@/pages/ObloanePage";
import DrepturiPage from "@/pages/DrepturiPage";
import BaghetePage from "@/pages/BaghetePage";
import SprosePage from "@/pages/SprosePage";
import PerimetruPage from "@/pages/PerimetruPage";
import ProfilePrelucratePage from "@/pages/ProfilePrelucratePage";
import ImaginiPage from "@/pages/ImaginiPage";
import ConfigurareObloanePage from "@/pages/obloane-config/ConfigurareObloanePage";
import PerimetruObloanePage from "@/pages/PerimetruObloanePage";

function FullScreenLoader() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
    </div>
  );
}

function Protected({ children, adminOnly, screen }) {
  const { user, ready, isAdmin, canAccess } = useAuth();
  if (!ready) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/comenzi" replace />;
  if (screen && !canAccess(screen)) return <Navigate to="/comenzi" replace />;
  return children;
}

function Shell({ children }) {
  const { collapsed } = useSidebar();
  return (
    <div className="min-h-screen bg-slate-100">
      <AppHeader />
      <main className={`${collapsed ? "ml-16" : "ml-60"} px-6 md:px-10 py-6 transition-[margin] duration-200`}>
        {children}
      </main>
    </div>
  );
}

function AppRoutes() {
  const { user, ready } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={ready && user ? <Navigate to="/comenzi" replace /> : <AmelLoginPage />} />
      <Route path="/comenzi" element={<Protected><Shell><ComenziPage /></Shell></Protected>} />
      <Route path="/comanda/:id" element={<Protected><Shell><ComandaDetaliuPage /></Shell></Protected>} />
      <Route path="/documente" element={<Protected><Shell><DocumentePage /></Shell></Protected>} />
      <Route path="/obloane" element={<Protected><Shell><ObloanePage /></Shell></Protected>} />
      <Route path="/baghete" element={<Protected screen="baghete"><Shell><BaghetePage /></Shell></Protected>} />
      <Route path="/sprose" element={<Protected screen="sprose"><Shell><SprosePage /></Shell></Protected>} />
      <Route path="/perimetru" element={<Protected screen="perimetru"><Shell><PerimetruPage /></Shell></Protected>} />
      <Route path="/prelucrate" element={<Protected screen="prelucrate"><Shell><ProfilePrelucratePage /></Shell></Protected>} />
      <Route path="/imagini" element={<Protected screen="imagini"><Shell><ImaginiPage /></Shell></Protected>} />
      <Route path="/configurare-obloane" element={<Protected screen="obloane_config"><Shell><ConfigurareObloanePage /></Shell></Protected>} />
      <Route path="/vopsire-obloane" element={<Protected screen="perimetru_obloane"><Shell><PerimetruObloanePage /></Shell></Protected>} />
      <Route path="/drepturi" element={<Protected adminOnly><Shell><DrepturiPage /></Shell></Protected>} />
      <Route path="*" element={<Navigate to="/comenzi" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SidebarProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </SidebarProvider>
    </AuthProvider>
  );
}

export default App;
